'use client';

import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { StarRating } from './StarRating';
import { useCreateReview, useUpdateReview } from '@/hooks/useReviews';
import { useNotification } from '@/providers/NotificationProvider';
import { Loader2 } from 'lucide-react';

const reviewSchema = z.object({
  rating: z.number().min(1, 'Please select a rating').max(5),
  reviewType: z.string().optional(),
  review: z
    .string()
    .trim()
    .min(10, 'Review must be at least 10 characters')
    .max(1000, 'Review must be less than 1000 characters'),
  productId: z.string().optional(),
  productVariantId: z.string().optional(),
});

type ReviewFormData = z.infer<typeof reviewSchema>;

interface ReviewFormProps {
  productId: string;
  productVariantId?: string;
  orderId: string;
  /** If provided the form operates in edit mode */
  reviewId?: string;
  initialValues?: { rating: number; reviewType?: string; review: string };
  onSuccess?: () => void;
  onCancel?: () => void;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

export function ReviewForm({
  productId,
  productVariantId,
  orderId,
  reviewId,
  initialValues,
  onSuccess,
  onCancel,
}: ReviewFormProps) {
  const isEdit = !!reviewId;
  const [rating, setRating] = useState(initialValues?.rating ?? 0);
  const { createReview, isLoading: createLoading } = useCreateReview();
  const { updateReview, isLoading: updateLoading } = useUpdateReview();
  const notification = useNotification();
  const isLoading = createLoading || updateLoading;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
    reset,
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    mode: 'onChange',
    defaultValues: {
      productId,
      productVariantId,
      rating: initialValues?.rating ?? 0,
      reviewType: initialValues?.reviewType ?? 'Product Review',
      review: initialValues?.review ?? '',
    },
  });

  const reviewText = watch('review') || '';

  const ratingLabel = useMemo(() => {
    return rating > 0 ? RATING_LABELS[rating] : '';
  }, [rating]);

  const onSubmit = async (data: ReviewFormData) => {
    try {
      if (isEdit && reviewId) {
        await updateReview(reviewId, {
          rating: data.rating,
          review: data.review,
          reviewType: data.reviewType ?? 'Product Review',
        });
        notification.success('Review updated', 'Your review has been updated.');
      } else {
        const reviewPayload = {
          rating: data.rating,
          review: data.review,
          reviewType: data.reviewType ?? 'Product Review',
          ...(data.productId && { productId: data.productId }),
          ...(data.productVariantId && { productVariantId: data.productVariantId }),
        };
        await createReview(orderId, reviewPayload);
        notification.success('Review submitted', 'Thank you for your feedback!');
      }

      reset();
      setRating(0);
      onSuccess?.();
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong. Please try again.';

      if (status === 403) {
        notification.error('Cannot submit review', 'You can only review your completed orders.');
      } else if (status === 400) {
        notification.error('Invalid review', errorMessage);
      } else {
        notification.error('Submission failed', errorMessage);
      }
    }
  };

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
    setValue('rating', newRating, { shouldValidate: true });
  };

  const isSubmitDisabled = isLoading || isSubmitting || !isValid || rating === 0;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
      aria-label="Product Review Form"
    >
      {/* Rating */}
      <div>
        <label className="block text-sm text-hsecondary/60 mb-3">
          Your Rating <span className="text-red-400">*</span>
        </label>

        <div className="flex items-center gap-4">
          <StarRating
            rating={rating}
            size="lg"
            interactive
            onChange={handleRatingChange}
          />

          {ratingLabel && (
            <span className="text-xs font-medium text-hsecondary/60 opacity-70">
              {ratingLabel}
            </span>
          )}
        </div>

        {errors.rating && (
          <p className="text-red-500 text-xs mt-2">
            {errors.rating.message}
          </p>
        )}
      </div>

      {/* Review Type */}
      <div>
        <label
          htmlFor="reviewType"
          className="block text-sm text-hsecondary/60 mb-2"
        >
          Review Type
        </label>

        <select
          id="reviewType"
          {...register('reviewType')}
          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-hprimary/30 focus:border-hprimary transition-colors"
        >
          <option value="Product Review">Product Review</option>
          <option value="Quality Review">Quality Review</option>
          <option value="Service Review">Service Review</option>
          <option value="Delivery Review">Delivery Review</option>
        </select>
      </div>

      {/* Review Text */}
      <div>
        <label
          htmlFor="review"
          className="block text-sm text-hsecondary/60 mb-2"
        >
          Your Review <span className="text-red-400">*</span>
        </label>

        <textarea
          id="review"
          {...register('review')}
          rows={5}
          maxLength={1000}
          placeholder="Share your experience with this product..."
          className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hprimary/30 focus:border-hprimary transition-colors resize-none"
        />

        <div className="flex justify-between mt-1.5 text-xs">
          {errors.review ? (
            <p className="text-red-500">{errors.review.message}</p>
          ) : (
            <span className="text-gray-400">Minimum 10 characters</span>
          )}
          <span className={reviewText.length > 900 ? 'text-orange-500' : 'text-gray-400'}>
            {reviewText.length}/1000
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-hsecondary px-6 py-2.5 text-sm font-semibold text-white hover:bg-hsecondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {isEdit ? 'Saving...' : 'Submitting...'}
            </>
          ) : (
            isEdit ? 'Save Changes' : 'Submit Review'
          )}
        </button>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-full border border-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 text-center"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
